/**
 * Text/code file renderer with fetch support and JSON pretty-printing
 */

import { useState, useEffect } from 'react';
import { Box, Stack, Text, Loader } from '@mantine/core';
import { Card } from '@/components/ui';

interface TextRendererProps {
  src?: string;
  fileName: string;
  textContent?: string;
}

const MAX_TEXT_SIZE = 1024 * 1024; // 1MB

/**
 * Check if content is likely binary by looking for a high ratio of
 * non-printable characters in the first 8KB.
 */
function isBinaryContent(text: string): boolean {
  const sample = text.slice(0, 8192);
  let nonPrintable = 0;
  for (let i = 0; i < sample.length; i++) {
    const code = sample.charCodeAt(i);
    // Allow tab (9), newline (10), carriage return (13), and printable ASCII (32-126)
    // Also allow common Unicode range (160+)
    if (code < 9 || (code > 13 && code < 32) || (code > 126 && code < 160)) {
      nonPrintable++;
    }
  }
  // If more than 10% of characters are non-printable, treat as binary
  return sample.length > 0 && nonPrintable / sample.length > 0.1;
}

export function TextRenderer({ src, fileName, textContent }: TextRendererProps) {
  const [content, setContent] = useState<string | null>(textContent || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (textContent) {
      setContent(textContent);
      return;
    }
    if (!src) {
      setError('No source URL provided');
      return;
    }

    setLoading(true);
    setError(null);

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);

    fetch(src, { signal: controller.signal })
      .then(async (res) => {
        if (!res.ok) throw new Error(`Failed to fetch: ${res.status}`);
        const size = Number(res.headers.get('content-length') || 0);
        if (size > MAX_TEXT_SIZE) throw new Error('File too large to preview (>1MB)');
        return res.text();
      })
      .then((text) => {
        // Reject binary content that was miscategorised as text
        if (isBinaryContent(text)) {
          throw new Error('This file contains binary content and cannot be previewed as text');
        }
        if (text.length > MAX_TEXT_SIZE) {
          text = text.slice(0, MAX_TEXT_SIZE) + '\n\n... (truncated)';
        }
        // Pretty-print JSON
        if (fileName.endsWith('.json') || fileName.endsWith('.jsonl')) {
          try {
            const parsed = JSON.parse(text);
            text = JSON.stringify(parsed, null, 2);
          } catch {
            // Not valid JSON, show as-is
          }
        }
        setContent(text);
      })
      .catch((err) => {
        if (err.name !== 'AbortError') {
          setError(err.message || 'Unable to load text content');
        }
      })
      .finally(() => {
        clearTimeout(timeout);
        setLoading(false);
      });

    return () => {
      controller.abort();
      clearTimeout(timeout);
    };
  }, [src, textContent, fileName]);

  if (loading) {
    return (
      <Stack align="center" justify="center" h="100%" gap="md">
        <Loader size="md" color="white" />
        <Text c="dimmed" size="sm">Loading text content...</Text>
      </Stack>
    );
  }

  if (error) {
    return (
      <Stack align="center" justify="center" h="100%" gap="md">
        <Text c="red.4" size="sm">{error}</Text>
      </Stack>
    );
  }

  return (
    <Box
      style={{
        width: '100%',
        height: '100%',
        overflow: 'auto',
        padding: 16,
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Card
        variant="elevated"
        p="md"
        style={{
          width: '100%',
          maxWidth: 900,
          backgroundColor: 'rgba(30, 30, 30, 0.95)',
          overflow: 'auto',
        }}
      >
        <pre
          style={{
            margin: 0,
            fontFamily: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
            fontSize: 13,
            lineHeight: 1.6,
            color: '#e0e0e0',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {content}
        </pre>
      </Card>
    </Box>
  );
}
